import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Select, Typography, Spin, Empty, Button } from 'antd';
import { GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import eventsService, { type EventWithCircuit } from '../../api/eventsService';
import seasonsService, { type Season } from '../../api/seasonsService';
import { circuitsService, type CircuitWithGeoJSON } from '../../api/circuitsService';
import { useTheme } from '../../contexts/useTheme';
import { useSeason } from '../../hooks/useSeason';
import { useEvent } from '../../hooks/useEvent';
import { usePermissions } from '../../hooks/usePermissions';
import { MapView, type MapViewRef } from '../../components/Map/MapView';
import { EventList } from '../../components/Map/EventList';
import { CreateEventModal } from '../../components/Map/CreateEventModal';
import { UpdateEventModal } from '../../components/Map/UpdateEventModal';
import { ROUTES } from '../../utils/constants';
import './EventsMap.css';

const { Sider, Content } = Layout;
const { Title } = Typography;

export const EventsMap = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | undefined>();
  const [events, setEvents] = useState<EventWithCircuit[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  const [circuitsCache, setCircuitsCache] = useState<Record<string, CircuitWithGeoJSON>>({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [eventToUpdate, setEventToUpdate] = useState<EventWithCircuit | null>(null);
  const { theme } = useTheme();
  const { setSelectedSeason: setGlobalSeason } = useSeason();
  const { setSelectedEvent: setGlobalEvent } = useEvent();
  const { canManageEvents } = usePermissions();
  const navigate = useNavigate();
  const mapRef = useRef<MapViewRef>(null);

  // Fetch seasons on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setSeasonsLoading(true);
        const response = await seasonsService.getAllSeasons();
        if (response.success && response.data && response.data.length > 0) {
          setSeasons(response.data);
          // Set the latest season as default (find the one with the highest year)
          const latestSeason = response.data.reduce((latest, season) => 
            season.year > latest.year ? season : latest
          );
          setSelectedSeason(latestSeason.year);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      } finally {
        setSeasonsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch events when season changes and preload circuit GeoJSON data
  useEffect(() => {
    if (!selectedSeason) return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await eventsService.getAllEvents(selectedSeason);
        if (response.success && response.data) {
          setEvents(response.data);
          
          // Preload all circuit GeoJSON data in parallel
          // Use circuit_geojson_id if available, otherwise fall back to circuit_id
          // Create unique entries based on effective geojson ID
          const geojsonEntries = response.data.map(e => ({
            circuitId: e.circuit_id,
            geojsonId: e.circuit_geojson_id || e.circuit_id,
          }));
          // Deduplicate by geojsonId
          const uniqueGeojsonIds = [...new Set(geojsonEntries.map(e => e.geojsonId))];
          const circuitPromises = uniqueGeojsonIds.map(geojsonId => {
            // Find the circuit_id for this geojson (could be different)
            const entry = geojsonEntries.find(e => e.geojsonId === geojsonId);
            const circuitId = entry?.circuitId || geojsonId;
            return circuitsService.getCircuitById(circuitId, geojsonId).catch(err => {
              console.error(`Failed to load circuit ${geojsonId}:`, err);
              return null;
            });
          });
          
          const circuits = await Promise.all(circuitPromises);
          const newCache: Record<string, CircuitWithGeoJSON> = {};
          // Cache by geojsonId so events can look up their specific layout
          uniqueGeojsonIds.forEach((geojsonId, index) => {
            const circuit = circuits[index];
            if (circuit) {
              newCache[geojsonId] = circuit;
            }
          });
          setCircuitsCache(newCache);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [selectedSeason]);

  const handleEventClick = (event: EventWithCircuit) => {
    setSelectedEventId(event.circuit_id);
  };

  const handleMarkerClick = async (event: EventWithCircuit) => {
    // Zoom to circuit and show popup (like "Show on Map" button)
    if (mapRef.current) {
      await mapRef.current.zoomToCircuit(event);
      setSelectedEventId(event.circuit_id);
    }
  };

  const handleGoToEvent = (event: EventWithCircuit) => {
    // Find the season object for this event
    const season = seasons.find(s => s.year === event.season_year);
    
    // Update global contexts
    if (season) {
      setGlobalSeason(season);
    }
    setGlobalEvent(event);
    
    // Navigate to dashboard
    navigate(ROUTES.CLIMATE_RECAP);
  };

  const handleShowOnMap = async (event: EventWithCircuit) => {
    if (mapRef.current) {
      await mapRef.current.zoomToCircuit(event);
      setSelectedEventId(event.circuit_id);
    }
  };

  const handleClearSelection = () => {
    setSelectedEventId(undefined);
  };

  const handleCreateEvent = () => {
    setCreateModalOpen(true);
  };

  const handleEditEvent = (event: EventWithCircuit) => {
    setEventToUpdate(event);
    setUpdateModalOpen(true);
  };

  const handleCreateSuccess = async () => {
    // Refresh events for current season
    if (selectedSeason) {
      try {
        setLoading(true);
        const response = await eventsService.getAllEvents(selectedSeason);
        if (response.success && response.data) {
          setEvents(response.data);
        }

        // Refresh seasons in case a new season was added
        const seasonsResponse = await seasonsService.getAllSeasons();
        if (seasonsResponse.success && seasonsResponse.data) {
          setSeasons(seasonsResponse.data);
        }
      } catch (error) {
        console.error('Failed to refresh events:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateSuccess = async () => {
    // Refresh events for current season
    if (selectedSeason) {
      try {
        setLoading(true);
        const response = await eventsService.getAllEvents(selectedSeason);
        if (response.success && response.data) {
          setEvents(response.data);
        }
      } catch (error) {
        console.error('Failed to refresh events:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Layout
      className="events-map-container"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      {/* Background overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(135deg, #0B1220 0%, #111827 100%)',
          zIndex: 0,
        }}
      />

      <Layout style={{ position: 'relative', zIndex: 1, background: 'transparent', width: '100%', display: 'flex', flex: 1 }}>
        {/* Map View */}
        <Content style={{ position: 'relative', overflow: 'hidden', flex: 1, height: '100%' }}>
          <MapView
            ref={mapRef}
            events={events}
            selectedEventId={selectedEventId}
            onMarkerClick={handleMarkerClick}
            onGoToEvent={handleGoToEvent}
            onClearSelection={handleClearSelection}
            circuitsCache={circuitsCache}
            theme={theme}
          />
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(11, 18, 32, 0.8)',
                zIndex: 10,
              }}
            >
              <Spin size="large" />
            </div>
          )}
        </Content>

        {/* Sidebar */}
        <Sider
          width={400}
          style={{
            background: theme === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderLeft: theme === 'dark' 
              ? '1px solid rgba(255, 255, 255, 0.1)' 
              : '1px solid rgba(0, 0, 0, 0.1)',
            height: '100%',
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '24px 20px',
              borderBottom: theme === 'dark' 
                ? '1px solid rgba(255, 255, 255, 0.1)' 
                : '1px solid rgba(0, 0, 0, 0.1)',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <GlobalOutlined
                  style={{ fontSize: '24px', color: '#0E447D' }}
                />
                <Title
                  level={3}
                  style={{
                    margin: 0,
                    color: theme === 'dark' ? '#fff' : '#000',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}
                >
                  Events Calendar
                </Title>
              </div>
              
              {/* Create Event Button - Only for root users */}
              {canManageEvents && (
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined />}
                  onClick={handleCreateEvent}
                  size="large"
                  title="Create New Event"
                />
              )}
            </div>

            {/* Season Selector */}
            <Select
              value={selectedSeason}
              onChange={setSelectedSeason}
              loading={seasonsLoading}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select Season"
              options={seasons.map((season) => ({
                label: season.name,
                value: season.year,
              }))}
              suffixIcon={
                <span style={{ color: '#0E447D' }}>▼</span>
              }
            />
          </div>

          {/* Events List - scrollable event list only */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 0',
            }}
          >
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '40px 0',
                }}
              >
                <Spin />
              </div>
            ) : events.length > 0 ? (
              <EventList
                events={events}
                selectedEventId={selectedEventId}
                onEventClick={handleEventClick}
                onGoToEvent={handleGoToEvent}
                onShowOnMap={handleShowOnMap}
                onEditEvent={canManageEvents ? handleEditEvent : undefined}
                theme={theme}
                circuitsCache={circuitsCache}
              />
            ) : (
              <Empty
                description="No events found"
                style={{ color: theme === 'dark' ? '#fff' : '#000', padding: '40px 0' }}
              />
            )}
          </div>
        </Sider>
      </Layout>

      {/* Create Event Modal */}
      <CreateEventModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Update Event Modal */}
      <UpdateEventModal
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setEventToUpdate(null);
        }}
        onSuccess={handleUpdateSuccess}
        event={eventToUpdate}
      />
    </Layout>
  );
};

